import React from "react";
import { RichTextEditor, type RichTextEditorRef } from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { Box } from "@mui/material";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  error?: boolean;
}

export function MyRichTextEditor({
  value,
  onChange,
  placeholder = "Enter your message content here...",
  error = false,
}: RichTextEditorProps) {
  const editorRef = React.useRef<RichTextEditorRef>(null);

  React.useEffect(() => {
    if (editorRef.current?.editor) {
      const currentContent = editorRef.current.editor.getHTML();
      if (value !== currentContent) {
        editorRef.current.editor.commands.setContent(value || "");
      }
    }
  }, [value]);

  const handleUpdate = React.useCallback(() => {
    if (editorRef.current?.editor) {
      onChange(editorRef.current.editor.getHTML());
    }
  }, [onChange]);

  return (
    <Box
      sx={{
        border: error ? "1px solid #ef4444" : "1px solid #e5e7eb",
        borderRadius: "8px",
        overflow: "hidden",
        "& .MuiRichTextEditor": {
          border: "none",
        },
        "& .MuiRichTextEditor-toolbar": {
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
        },
        "& .MuiRichTextEditor-content": {
          minHeight: "200px",
          maxHeight: "400px",
          overflowY: "auto",
        },
      }}
    >
      <RichTextEditor
        ref={editorRef}
        extensions={[
          StarterKit.configure({
            heading: false,
          }),
          Placeholder.configure({
            placeholder,
          }),
          TextStyle,
          Color,
          Underline,
          Link.configure({
            openOnClick: false,
            HTMLAttributes: {
              class: "text-blue-600 underline",
            },
          }),
          Highlight,
          TextAlign.configure({ types: ["paragraph", "heading"] }),
          Table.configure({ resizable: true }),
          TableRow,
          TableHeader,
          TableCell,
        ]}
        onUpdate={handleUpdate}
      />
    </Box>
  );
}
